import { useEffect, useRef, useState } from 'react';
import { Button, LicensePlateInput } from '../Index';
import CloseIcon from '../../assets/Icon/close.svg';
import { registerVehicleForCustomer } from '../../services/api/customerApi';
import { extractErrorMessage } from '../../services/api/errorHandler';
import VehicleColorPicker from './VehicleColorPicker';
import VehicleFormField from './VehicleFormField';
import VehicleYearPicker from './VehicleYearPicker';
import { EMPTY_VEHICLE_FORM, groupThousands, parsePlateString, toEnglishDigits, toPersianDigits, validateVehicleForm } from './vehicleRegisterUtils';
import './vehicleRegisterPopup.scss';

function VehicleHeaderIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 17h14M6 17l-1-4 2-5h10l2 5-1 4M8 17v2m8-2v2M7 13h.01M17 13h.01" /></svg>;
}

function FormSection({ children }) {
  return <section className="veh-reg__section"><div className="veh-reg__section-body">{children}</div></section>;
}

export default function VehicleRegisterPopup({ isOpen, onClose, customerId = null, customerPhone = '', initialPlate = '', onSuccess }) {
  const [form, setForm] = useState(EMPTY_VEHICLE_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mileageInputRef = useRef(null);
  const mileageCaretRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...EMPTY_VEHICLE_FORM, plate: initialPlate || '' });
    setErrors({});
    setSubmitError('');
    setSubmitting(false);
  }, [isOpen, initialPlate]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', handleEscape); };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (mileageCaretRef.current == null) return;
    const position = mileageCaretRef.current;
    mileageCaretRef.current = null;
    const input = mileageInputRef.current;
    if (input && document.activeElement === input) input.setSelectionRange(position, position);
  }, [form.currentMileage]);

  if (!isOpen) return null;

  const update = (field, value) => {
    setForm(previous => ({ ...previous, [field]: String(value) }));
    setErrors(previous => previous[field] ? { ...previous, [field]: undefined } : previous);
    setSubmitError('');
  };

  const validateField = field => {
    const fieldErrors = validateVehicleForm(form);
    if (fieldErrors[field]) setErrors(previous => ({ ...previous, [field]: fieldErrors[field] }));
  };

  const handleMileage = event => {
    const raw = event.target.value;
    const cursor = event.target.selectionStart ?? raw.length;
    const digitsBeforeCursor = toEnglishDigits(raw.slice(0, cursor)).replace(/[^\d]/g, '').length;
    const digits = toEnglishDigits(raw).replace(/[^\d]/g, '').slice(0, 10).replace(/^0+(?=\d)/, '');
    const formatted = digits ? toPersianDigits(groupThousands(digits)) : '';
    if (digitsBeforeCursor < digits.length) {
      let position = 0;
      let seen = 0;
      while (position < formatted.length && seen < digitsBeforeCursor) { if (/[۰-۹]/.test(formatted[position])) seen += 1; position += 1; }
      mileageCaretRef.current = position;
    }
    update('currentMileage', formatted);
  };

  const updateYears = ({ jalali, gregorian }) => {
    setForm(previous => ({ ...previous, productionYearJalali: jalali, productionYearGregorian: gregorian }));
    setErrors(previous => ({ ...previous, productionYearJalali: undefined, productionYearGregorian: undefined }));
    setSubmitError('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;
    const nextErrors = validateVehicleForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const plate = parsePlateString(form.plate);
      const vehicle = await registerVehicleForCustomer(customerId, {
        title: form.carModel.trim(),
        brand: form.carModel.trim().split(' ')[0] || 'Unknown',
        currentMileage: parseInt(toEnglishDigits(form.currentMileage), 10) || 0,
        buildYear: parseInt(toEnglishDigits(form.productionYearGregorian), 10) || null,
        color: form.carColor.trim() || null,
        plate1: plate.plate1 || null, plate2: plate.plate2 || null,
        plate3: plate.plate3 || null, plateEnd: plate.plateEnd || null,
        image: null,
      });
      onSuccess?.(vehicle);
      onClose();
    } catch (error) {
      setSubmitError(extractErrorMessage(error, 'خطا در ثبت خودرو. لطفاً دوباره تلاش کنید'));
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="vehicle-register-pop-overlay" onClick={onClose}>
    <div className="vehicle-register-pop-container" onClick={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="ثبت خودروی جدید">
      <header className="vehicle-register-pop-header">
        <span className="vehicle-register-pop-icon"><VehicleHeaderIcon /></span>
        <div className="vehicle-register-pop-titles"><h2>ثبت خودروی جدید</h2><p>{customerPhone ? `مشتری: ${customerPhone}` : 'اطلاعات خودرو را کامل کنید'}</p></div>
        <button className="vehicle-register-pop-close" onClick={onClose} aria-label="بستن" type="button"><img src={CloseIcon} alt="" /></button>
      </header>

      <form className="vehicle-register-pop-body" onSubmit={handleSubmit} noValidate>
        <div className="vehicle-register-pop-scroll">
          <FormSection number="1" title="مشخصات خودرو" description="مدل، سال ساخت و کیلومتر فعلی">
            <VehicleFormField name="carModel" label="مدل خودرو" value={form.carModel} onChange={event => update('carModel', event.target.value)} onBlur={() => validateField('carModel')} placeholder="مثال: پژو ۲۰۶ تیپ ۵" disabled={submitting} error={errors.carModel} />
            <VehicleYearPicker jalaliValue={form.productionYearJalali} gregorianValue={form.productionYearGregorian} errors={errors} disabled={submitting} onChange={updateYears} />
            <VehicleFormField name="currentMileage" label="کیلومتر فعلی" value={form.currentMileage} onChange={handleMileage} onBlur={() => validateField('currentMileage')} placeholder="مثال: ۵۰٬۰۰۰" numeric maxLength={13} disabled={submitting} error={errors.currentMileage} inputRef={mileageInputRef} />
          </FormSection>

          <FormSection number="2" title="ظاهر و پلاک" description="رنگ خودرو و شماره پلاک">
            <VehicleColorPicker value={form.carColor} disabled={submitting} onChange={value => update('carColor', value)} />
            <div className="veh-reg__field"><span className="veh-reg__label">شماره پلاک</span><LicensePlateInput value={form.plate} onChange={value => update('plate', value)} disabled={submitting} hasError={Boolean(errors.plate)} />{errors.plate && <p className="veh-reg__field-error">{errors.plate}</p>}</div>
          </FormSection>
        </div>

        <footer className="vehicle-register-pop-footer">
          {submitError && <p className="veh-reg__submit-error" role="alert">{submitError}</p>}
          <Button size="full" color="dark" disabled={submitting} type="submit">{submitting ? 'در حال ثبت...' : 'ثبت خودرو'}</Button>
        </footer>
      </form>
    </div>
  </div>;
}
